import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { BsGoogle, BsMicrosoft } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Button, Divider, Flex, Text, VStack } from '@chakra-ui/react';
import userflow from 'userflow.js';

import FormInput from '../../components/form/FormInput';
import LogoBox from '../../components/Header/LogoBox';
import { CypressIds } from '../../constants/cypressIds';
import { userflowConstants } from '../../constants/userflowConstants';
import loginBackground from '../../loginBackground.png';
import { createErrorToast, createSuccessToast, createToast } from '../../redux/modules/app';
import { useMeCompanies, useMeData } from '../../services/user-service';
import boxStyles from '../../styles/boxStyles';
import colors from '../../styles/theme/foundation/colors';
import { stringFromArray } from '../../utils/arrayUtil';

const Login = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { data: user, isLoading: userIsLoading, refetch: refetchMe } = useMeData();
  const { data: companies, isLoading: companiesAreLoading, refetch: refetchCompanies } = useMeCompanies();

  // identify anonymous users for userFlow
  useEffect(() => {
    userflow.init(userflowConstants.token);

    if (user) {
      userflow.identify(user.userId, {
        user_id: user.userId,
        name: `${user.userFirstName} ${user.userLastName}`,
        email: user.userEmail,
        userLevel: user.userLevel,
        userRoles: stringFromArray(user?.roles ?? [], ', ', 'displayName'),
        pinsVersion: { set_once: '2.0' },
        last_signed_in_at: new Date().toISOString() // ISO 8601 format
      });
    }
  }, [user]);

  // look for microsoft admin consent or sso errors
  const urlQueryParams = new URLSearchParams(window.location.search);
  const microsoftAdminConsent = urlQueryParams.get('microsoft_admin_consent');
  const errors = urlQueryParams.get('error');

  useEffect(() => {
    if (microsoftAdminConsent === 'success') {
      dispatch(createSuccessToast({ title: 'Microsoft Admin Consent Successful', description: 'You can now log in to PINS.' }));
    }

    if (errors === 'access_denied') {
      dispatch(createErrorToast({ title: 'SSO Access Denied', description: 'Failed to gain access from Microsoft' }));
    }

    if (errors === 'consent_required') {
      dispatch(createErrorToast({ title: 'SSO Access Denied', description: 'Admin Consent Required' }));
    }

    return () => {};
  }, [errors, microsoftAdminConsent]);

  // if user is already logged in, send them to the Reports Page
  useEffect(() => {
    if (!userIsLoading && !companiesAreLoading && user?.userId && companies.length) {
      if (user?.userLevel === 1) {
        // pins admin, send to pinsadmin page
        history.push('pinsadmin');

        return;
      }

      // redirect user to the Reports Page of their first company
      const firstCompany = companies[0];
      const firstCompanyId = firstCompany?.companyId;

      if (firstCompanyId) {
        // window.location.replace(`companies/${firstCompanyId}/departments`);
        history.push(`companies/${firstCompanyId}/departments`);

        return;
      }
    }

    // if a user exists without any companies
    if (user?.userId && !companiesAreLoading && !companies.length) {
      dispatch(createErrorToast({
        title: 'No Active Company Roles Found for this user',
        description: 'PINS could not find any companies for this user.  Please reach out to your company admin'
      }));
    }

    // keep them here on log in page
  }, [user, userIsLoading, companies, companiesAreLoading]);

  const useFormMethods = useForm();
  const { handleSubmit } = useFormMethods;

  const onSubmit = async data => {
    const npLogin = await fetch('/api/v1/login', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: data.email_address,
        password: data.password
      })
    });

    if (npLogin.status === 503) {
      dispatch(createToast({ title: 'PINS Is Currently Undergoing Maintenance', status: 'warning' }));

      return;
    }

    if (!npLogin.ok || npLogin.status !== 204) {
      dispatch(createErrorToast({ title: 'Invalid Login Credentials' }));

      return;
    }

    dispatch(createSuccessToast({ title: 'Success!  Logging you in now!', duration: 3000 }));

    // refetch me which redirects the user
    refetchMe();
    refetchCompanies();
  };

  return (
    <Box
      backgroundImage={loginBackground}
      backgroundPosition="center"
      backgroundSize="cover"
      h="100vh"
      position="relative"
      w="100vw"
    >
      <Box
        h="40px"
        left={6}
        position="absolute"
        top={6}
      >
        <LogoBox to="/" />
      </Box>
      <Box
        alignItems="center"
        display="flex"
        h="100%"
        justifyContent="center"
        p={6}
      >
        <Box
          bgColor={colors.white}
          borderRadius="sm"
          boxShadow={boxStyles.boxShadow}
          mx="auto"
          my="auto"
          p={8}
          w="450px"
        >
          <Text
            mb={4}
            textAlign="center"
            textStyle="displayXs"
          >
            Welcome Back!
          </Text>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box mb={2}>
              <FormInput
                cypressId={CypressIds.loginEmail}
                inputName="email_address"
                labelName="Email Address"
                placeholder="Email Address"
                useFormMethods={useFormMethods}
                validationRules={{ required: 'Please provide an email address.' }}
              />
              <FormInput
                cypressId={CypressIds.loginPassword}
                inputName="password"
                labelName="Password"
                placeholder="Password"
                type="password"
                useFormMethods={useFormMethods}
                validationRules={{ required: 'Please provide a password.' }}
              />
            </Box>

            <Button
              data-cy={CypressIds.loginButton}
              mb={4}
              type="submit"
              w="100%"
            >
              Log In
            </Button>
          </form>
          <Button
            mb={4}
            variant="textPrimary"
            w="100%"
            onClick={() => history.push('forgot-password')}
          >
            Forgot Password
          </Button>

          <Flex align="center" mb={6}>
            <Divider borderColor="gray.600" />
            <Text color="gray.600" fontSize="sm" mx={3}>
              OR
            </Text>
            <Divider borderColor="gray.600" />
          </Flex>

          <VStack>
            <Button
              data-cy={CypressIds.loginGoogleSSOButton}
              leftIcon={<BsGoogle />}
              my={1}
              variant="secondary"
              onClick={() => {
                window.location.href = `${window.location.origin}/api/v1/auth/google/redirect`;
              }}
            >
              Login with Google
            </Button>

            <Button
              data-cy={CypressIds.loginMicrosoftSSOButton}
              leftIcon={<BsMicrosoft />}
              my={1}
              variant="secondary"
              onClick={() => {
                window.location.href = `${window.location.origin}/api/v1/auth/microsoft/redirect`;
              }}
            >
              Login with Outlook
            </Button>
          </VStack>

        </Box>
      </Box>
    </Box>
  );
};

export default Login;
